import React from "react";
import { Link } from "react-router-dom";
import { company, navLinks } from "../data/mock";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { useQuote } from "../context/QuoteContext";
import { InstallWarranty } from "./InstallWarranty";

const Footer = () => {
  const { openQuote } = useQuote();
  return (
    <footer className="bg-[#081832] text-slate-300">
      {/* CTA band */}
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
        <div className="-mt-px" />
        <div className="relative rounded-3xl bg-gradient-to-r from-[#1b3a6b] to-[#0a1f44] p-8 md:p-12 -translate-y-4 md:-translate-y-8 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-white">
              Ready to bring your space to life?
            </h3>
            <p className="text-slate-300 mt-2">
              Get a free, no-obligation quote for your home or business from our expert AV team today.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => openQuote()}
              className="px-6 py-3.5 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={company.phoneLink}
              className="px-6 py-3.5 rounded-full bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition-colors"
            >
              Call or text {company.phone}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-10 -mt-6">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <div className="inline-flex items-center bg-white/95 rounded-xl p-3 mb-4">
              <img
                src={company.logo}
                alt="Set Up Home Theater"
                className="h-16 w-auto object-contain"
              />
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              Commercial &amp; residential AV sales and installation (TV, audio and
              home cinema) across Rocky Mount and Nash County, North Carolina. We also
              travel beyond Nash County for a small mileage fee.
            </p>
            <div className="flex gap-3 mt-5">
              {[
                { Ic: Facebook, label: "Facebook", href: "https://www.facebook.com/setuphometheater" },
                { Ic: Instagram, label: "Instagram", href: "https://www.instagram.com/setuphometheater" },
              ].map(({ Ic, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#3b9dff] transition-colors"
                >
                  <Ic className="w-4 h-4 text-white" />
                </a>
              ))}
            </div>
            <a href={company.googleReview} target="_blank" rel="noopener noreferrer" data-testid="footer-leave-review-btn" className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-[#7dc4ff] hover:text-white transition-colors">★ Leave us a Google review</a>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              {navLinks.map((l) => (
                <li key={l.label}>
                  {l.external ? (
                    <a href={l.to} className="hover:text-[#3b9dff] transition-colors">
                      {l.label}
                    </a>
                  ) : (
                    <Link to={l.to} className="hover:text-[#3b9dff] transition-colors">
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Services</h4>
            <ul className="space-y-2.5 text-sm">
              {navLinks[2].children.map((c) => (
                <li key={c.to}>
                  <Link to={c.to} className="hover:text-[#3b9dff] transition-colors">
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Get in touch</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#3b9dff] mt-0.5" />
                <div>
                  <a href={company.phoneLink} data-testid="footer-phone" className="hover:text-[#3b9dff] font-medium">{company.phone}</a>
                  <span className="block text-xs text-slate-400">Call or text</span>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#3b9dff]" />
                <a href={`mailto:${company.email}`} className="hover:text-[#3b9dff]">
                  {company.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-[#3b9dff]" />
                <span>{company.location}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-8">
          <p className="text-slate-400 text-sm uppercase tracking-wide text-center mb-6">Proudly partnered with</p>
          <div className="flex justify-center" data-testid="partners">
            <a
              href="https://www.ironvalorarmory.com"
              target="_blank"
              rel="noopener"
              data-testid="iron-valor-link"
              className="flex flex-col items-center text-center rounded-xl overflow-hidden ring-1 ring-white/10 hover:ring-[#c8a24a]/60 transition-all hover:-translate-y-0.5 max-w-sm"
              aria-label="Iron Valor Armory, Rocky Mount, NC gunsmith (opens in a new tab)"
            >
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_home-cinema-build/artifacts/kxfcbdlk_IVA%20Logo.webp"
                alt="Iron Valor Armory, Type 07 FFL gunsmith in Rocky Mount, NC"
                className="w-full max-w-[300px] h-auto block"
                loading="lazy"
              />
              <p className="text-xs text-slate-400 leading-relaxed px-4 py-3">
                Iron Valor Armory is a Rocky Mount, NC gunsmith, forged in service and built for precision.
              </p>
            </a>
          </div>
        </div>

        <InstallWarranty dark testId="footer-install-warranty" className="border-t border-white/10 mt-8 pt-6" />

        <div className="border-t border-white/10 mt-8 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-400">
          <p>© {new Date().getFullYear()} Set Up Home Theater. All rights reserved.</p>
          <p>Rocky Mount &amp; Nash County, North Carolina</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
